import { useMemo } from 'react';

import { useIsTutorialMode } from '../../../context/PatientDataContext';
import type { InsuranceInfo } from '../../../types/types';
import type { CreatePatientCoverageRequest, OrganizedCoverages, PatientCoverage } from '../types';

import { useGetPatientCoveragesQuery } from './api';

export const normalizeCoverageKind = (kind?: string | null): string =>
  kind?.trim().toLowerCase().replace('-', '_') || 'insurance';

export const isSelfPayCoverage = (coverage?: Partial<PatientCoverage> | null): boolean =>
  normalizeCoverageKind(coverage?.kind) === 'self_pay';

export const getCoverageDisplayLabel = (
  coverage?: Partial<PatientCoverage & { display_label?: string | null }> | null,
): string | null => {
  if (!coverage) return null;
  if (isSelfPayCoverage(coverage)) return 'Self-pay';
  return coverage.display_label ?? coverage.payer_name ?? null;
};

export const buildSelfPayCoverageRequest = (coverageOrder = 1): CreatePatientCoverageRequest => ({
  coverage_order: coverageOrder,
  kind: 'self_pay',
  status: 'active',
  payer_id: null,
  payer_name: null,
  member_id: null,
  group_number: null,
  plan_name: null,
  relationship_to_subscriber: 'self',
  consent_on_file: false,
});

export const buildPendingInsuranceCoverageRequest = (
  coverageOrder = 1,
): CreatePatientCoverageRequest => ({
  coverage_order: coverageOrder,
  kind: 'insurance',
  status: 'pending',
  payer_id: null,
  payer_name: null,
  member_id: null,
  group_number: null,
  plan_name: null,
  relationship_to_subscriber: 'self',
  consent_on_file: false,
});

/**
 * Helper to convert empty/falsy values to null (for API)
 */
const toNullable = <T>(value: T | undefined | null | ''): T | null =>
  value === '' || value === undefined ? null : (value);

/**
 * Maps insurance data (legacy InsuranceInfo or PatientCoverage) to a coverage request format.
 * Use this when creating or updating coverages to ensure consistent field mapping.
 *
 * @param coverageOrder - 1 for primary, 2 for secondary, 3 for tertiary
 * @param insurance - The insurance data to map (can be legacy or new format)
 * @returns A properly formatted coverage request object
 */
export function mapInsuranceToCoverageRequest(
  coverageOrder: number,
  insurance: InsuranceInfo | PatientCoverage,
): CreatePatientCoverageRequest {
  // Helper to check if data is new format
  const isPatientCoverage = (data: InsuranceInfo | PatientCoverage): data is PatientCoverage =>
    'patient_insurance_coverage_id' in data;

  return {
    coverage_order: coverageOrder,
    kind: isPatientCoverage(insurance) ? normalizeCoverageKind(insurance.kind) : 'insurance',
    payer_id: toNullable(insurance.payer_id),
    payer_name: toNullable(insurance.payer_name),
    plan_name: toNullable('plan_name' in insurance ? insurance.plan_name : null),
    member_id: toNullable(
      'member_id' in insurance
        ? insurance.member_id
        : 'policy_number' in insurance
          ? insurance.policy_number
          : null,
    ),
    group_number: toNullable(insurance.group_number),
    relationship_to_subscriber:
      toNullable(
        'relationship_to_subscriber' in insurance
          ? insurance.relationship_to_subscriber
          : 'relationship_to_insured' in insurance
            ? insurance.relationship_to_insured
            : null,
      ) || 'self',
    consent_on_file:
      ('consent_on_file' in insurance
        ? insurance.consent_on_file
        : 'authorization_on_file' in insurance
          ? insurance.authorization_on_file
          : undefined) ?? undefined,
    subscriber_first_name: toNullable(
      'subscriber_first_name' in insurance ? insurance.subscriber_first_name : null,
    ),
    subscriber_last_name: toNullable(
      'subscriber_last_name' in insurance ? insurance.subscriber_last_name : null,
    ),
    subscriber_dob: toNullable(
      'subscriber_dob' in insurance
        ? insurance.subscriber_dob
        : 'insured_dob' in insurance
          ? insurance.insured_dob
          : null,
    ),
    subscriber_sex: toNullable(
      'subscriber_sex' in insurance
        ? insurance.subscriber_sex
        : 'insured_sex' in insurance
          ? insurance.insured_sex
          : null,
    ),
    subscriber_address: toNullable(
      'subscriber_address' in insurance
        ? insurance.subscriber_address
        : 'insured_address' in insurance
          ? insurance.insured_address
          : null,
    ),
  };
}

/**
 * Hook to get patient coverages organized by order (primary, secondary, tertiary)
 */
export function useOrganizedPatientCoverages(patientId: string) {
  const isTutorialMode = useIsTutorialMode();
  const { data, isLoading, error, refetch } = useGetPatientCoveragesQuery(
    { patientId },
    { skip: isTutorialMode },
  );

  const organized = useMemo<OrganizedCoverages>(() => {
    if (!data?.coverages) {
      return {};
    }

    return {
      primary: data.coverages.find(c => c.coverage_order === 1),
      secondary: data.coverages.find(c => c.coverage_order === 2),
      tertiary: data.coverages.find(c => c.coverage_order === 3),
    };
  }, [data]);

  return {
    billWithJotpsychResolved: data?.bill_with_jotpsych_resolved ?? null,
    billWithJotpsychSource: data?.bill_with_jotpsych_source ?? null,
    coverages: data?.coverages || [],
    organized,
    isLoading,
    error,
    refetch,
    total: data?.total || 0,
  };
}

/**
 * Hook to check if a patient has any active coverages
 */
export function useHasActiveCoverages(patientId: string) {
  const isTutorialMode = useIsTutorialMode();
  const { data, isLoading } = useGetPatientCoveragesQuery({ patientId }, { skip: isTutorialMode });

  const hasActive = useMemo(() => {
    if (!data?.coverages) return false;
    return data.coverages.some(c => c.status === 'active');
  }, [data]);

  return {
    hasActive,
    isLoading,
    count: data?.coverages?.filter(c => c.status === 'active').length || 0,
  };
}

/**
 * Helper to determine if a coverage needs subscriber information
 */
export function needsSubscriberInfo(coverage: Partial<PatientCoverage>): boolean {
  return coverage.relationship_to_subscriber !== 'self' && !!coverage.relationship_to_subscriber;
}

/**
 * Helper to get a display name for coverage order
 */
export function getCoverageOrderLabel(order: number): string {
  switch (order) {
    case 1:
      return 'Primary';
    case 2:
      return 'Secondary';
    case 3:
      return 'Tertiary';
    default:
      return `Coverage ${order}`;
  }
}
